import { AlertTriangle, CheckCircle2, Flag, TrendingUp } from 'lucide-react';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

import type { GoalStats } from '../metas.types';

interface MetasStatsProps {
  stats: GoalStats | undefined;
  loading: boolean;
  className?: string;
}

interface CeldaProps {
  icon: typeof Flag;
  label: string;
  value: string;
  hint: string;
  tone: 'brand' | 'emerald' | 'amber' | 'ink';
}

const TONES: Record<CeldaProps['tone'], string> = {
  brand: 'bg-brand-50 text-brand-600',
  emerald: 'bg-emerald-50 text-emerald-600',
  amber: 'bg-amber-50 text-amber-600',
  ink: 'bg-ink-100 text-ink-600',
};

function Celda({ icon: Icon, label, value, hint, tone }: CeldaProps) {
  return (
    <div className="flex items-start gap-3 bg-white px-4 py-3.5">
      <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-xl', TONES[tone])}>
        <Icon className="size-4.5" aria-hidden="true" />
      </span>

      <div className="min-w-0">
        <p className="font-mono text-lg font-bold tracking-tight text-ink-900 tabular-nums">{value}</p>
        <p className="truncate text-xs text-ink-500">{label}</p>
        <p className="line-clamp-2 text-[11px] text-ink-400">{hint}</p>
      </div>
    </div>
  );
}

/** Indicadores del sistema de metas. */
export function MetasStats({ stats, loading, className }: MetasStatsProps) {
  if (loading) {
    return (
      <div className={cn('grid gap-px overflow-hidden rounded-card border border-ink-100 bg-ink-100 sm:grid-cols-2 xl:grid-cols-4', className)}>
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} className="h-20 rounded-none" />
        ))}
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className={cn('grid gap-px overflow-hidden rounded-card border border-ink-100 bg-ink-100 sm:grid-cols-2 xl:grid-cols-4', className)}>
      <Celda
        icon={Flag}
        label="Metas activas"
        value={String(stats.total_active)}
        hint="Vigentes en este momento"
        tone="brand"
      />
      <Celda
        icon={CheckCircle2}
        label="Metas cumplidas"
        value={String(stats.total_completed)}
        hint="Histórico del sistema"
        tone="emerald"
      />
      <Celda
        icon={AlertTriangle}
        label="En riesgo"
        value={String(stats.at_risk)}
        hint="Vencen en ≤ 7 días y bajo el 50 %"
        tone="amber"
      />
      <Celda
        icon={TrendingUp}
        label="Avance promedio"
        value={`${Number(stats.avg_completion ?? 0).toFixed(1)}%`}
        hint="De las metas activas"
        tone="ink"
      />
    </div>
  );
}
